// Analytics utility functions for GA4 event tracking

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    GA_MEASUREMENT_ID: string;
    ENVIRONMENT: string;
  }
}

// Check if GA4 is available
const isGA4Available = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function' && 
         Boolean(window.GA_MEASUREMENT_ID) && 
         window.GA_MEASUREMENT_ID !== '%REACT_APP_GA_MEASUREMENT_ID%';
};

// Get additional context data
const getContextData = () => {
  return {
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    page_url: window.location.href,
    page_title: document.title,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    environment: window.ENVIRONMENT || 'production'
  };
};

// Generic event tracking function
export const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
  if (!isGA4Available()) {
    if (window.ENVIRONMENT === 'development') {
      console.log('GA4 not available, event not tracked:', eventName, parameters);
    }
    return;
  }

  const contextData = getContextData();
  const eventData = {
    ...parameters,
    ...contextData
  };

  // Send event to GA4
  window.gtag('event', eventName, eventData);

  // Log in development mode
  if (window.ENVIRONMENT === 'development') {
    console.log('GA4 Event Tracked:', {
      event: eventName,
      parameters: eventData
    });
  }
};

// Button click tracking functions
export const trackScheduleDemoClick = (buttonLocation: string, additionalData: Record<string, any> = {}) => {
  trackEvent('schedule_demo_clicked', {
    button_location: buttonLocation,
    event_category: 'engagement',
    event_label: 'demo_request',
    ...additionalData
  });
};

export const trackFreeTrialClick = (buttonLocation: string, additionalData: Record<string, any> = {}) => {
  trackEvent('free_trial_clicked', {
    button_location: buttonLocation,
    event_category: 'conversion',
    event_label: 'trial_signup',
    ...additionalData
  });
};

export const trackGetQuoteClick = (buttonLocation: string, additionalData: Record<string, any> = {}) => {
  trackEvent('get_quote_clicked', {
    button_location: buttonLocation,
    event_category: 'engagement',
    event_label: 'quote_request',
    ...additionalData
  });
};

// Form submission tracking functions
export const trackContactFormSubmitted = (formType: 'demo' | 'trial' | 'quote', formData: Record<string, any> = {}) => {
  trackEvent('contact_form_submitted', {
    form_type: formType,
    event_category: 'form_submission',
    event_label: `${formType}_form_success`,
    has_message: Boolean(formData.message && formData.message.trim()),
    form_completion_time: formData.completionTime || null
  });
};

export const trackContactFormError = (formType: 'demo' | 'trial' | 'quote', errorMessage: string, formData: Record<string, any> = {}) => {
  trackEvent('contact_form_error', {
    form_type: formType,
    error_message: errorMessage,
    event_category: 'form_submission',
    event_label: `${formType}_form_error`,
    has_message: Boolean(formData.message && formData.message.trim())
  });
};

// Generic button click tracking
export const trackButtonClick = (buttonText: string, buttonLocation: string, additionalData: Record<string, any> = {}) => {
  trackEvent('button_click', {
    button_text: buttonText,
    button_location: buttonLocation,
    event_category: 'engagement',
    ...additionalData
  });
};

// Audio player tracking functions
export const trackAudioPlayerStart = (audioTitle: string, audioSrc: string, additionalData: Record<string, any> = {}) => {
  trackEvent('audio_player_start', {
    audio_title: audioTitle,
    audio_src: audioSrc,
    event_category: 'audio_engagement',
    event_label: 'audio_play_started',
    play_timestamp: new Date().toISOString(),
    ...additionalData
  });
};

export const trackAudioPlayerPause = (audioTitle: string, audioSrc: string, currentTime: number, duration: number, additionalData: Record<string, any> = {}) => {
  trackEvent('audio_player_pause', {
    audio_title: audioTitle,
    audio_src: audioSrc,
    current_time: Math.round((currentTime / 60) * 100) / 100, // Convert to minutes
    duration: Math.round((duration / 60) * 100) / 100,        // Convert to minutes
    progress_percentage: Math.round((currentTime / duration) * 100),
    event_category: 'audio_engagement',
    event_label: 'audio_paused',
    pause_timestamp: new Date().toISOString(),
    ...additionalData
  });
};

export const trackAudioPlayerSeek = (audioTitle: string, audioSrc: string, fromTime: number, toTime: number, duration: number, additionalData: Record<string, any> = {}) => {
  trackEvent('audio_player_seek', {
    audio_title: audioTitle,
    audio_src: audioSrc,
    from_time: Math.round((fromTime / 60) * 100) / 100, // Convert to minutes
    to_time: Math.round((toTime / 60) * 100) / 100,     // Convert to minutes
    duration: Math.round((duration / 60) * 100) / 100,  // Convert to minutes
    from_progress_percentage: Math.round((fromTime / duration) * 100),
    to_progress_percentage: Math.round((toTime / duration) * 100),
    seek_direction: toTime > fromTime ? 'forward' : 'backward',
    seek_distance: Math.round((Math.abs(toTime - fromTime) / 60) * 100) / 100, // Convert to minutes
    event_category: 'audio_engagement',
    event_label: 'audio_seeked',
    seek_timestamp: new Date().toISOString(),
    ...additionalData
  });
};

// Page view tracking (if needed)
export const trackPageView = (pageName?: string) => {
  if (!isGA4Available()) return;
  
  window.gtag('config', window.GA_MEASUREMENT_ID, {
    page_title: pageName || document.title,
    page_location: window.location.href
  });
  
  if (window.ENVIRONMENT === 'development') {
    console.log('GA4 Page View Tracked:', {
      page_title: pageName || document.title,
      page_location: window.location.href
    });
  }
};

// ATS Score tracking functions
export const trackATSScoreNavigation = (source: 'header' | 'promo_section' | 'direct', additionalData: Record<string, any> = {}) => {
  trackEvent('ats_score_navigation', {
    navigation_source: source,
    event_category: 'ats_score',
    event_label: `navigated_from_${source}`,
    ...additionalData
  });
};

export const trackATSScoreAnalysisStarted = (hasJobDescription: boolean, fileSize: number, additionalData: Record<string, any> = {}) => {
  trackEvent('ats_score_analysis_started', {
    has_job_description: hasJobDescription,
    file_size_bytes: fileSize,
    file_size_kb: Math.round(fileSize / 1024),
    event_category: 'ats_score',
    event_label: 'analysis_started',
    ...additionalData
  });
};

export const trackATSScoreAnalysisSuccess = (score: number, hasJobDescription: boolean, strengthsCount: number, improvementsCount: number, additionalData: Record<string, any> = {}) => {
  trackEvent('ats_score_analysis_success', {
    score: score,
    score_range: score >= 80 ? 'high' : score >= 60 ? 'medium' : 'low',
    has_job_description: hasJobDescription,
    strengths_count: strengthsCount,
    improvements_count: improvementsCount,
    event_category: 'ats_score',
    event_label: 'analysis_success',
    ...additionalData
  });
};

export const trackATSScoreAnalysisError = (errorType: string, errorMessage: string, hasJobDescription: boolean, additionalData: Record<string, any> = {}) => {
  trackEvent('ats_score_analysis_error', {
    error_type: errorType,
    error_message: errorMessage,
    has_job_description: hasJobDescription,
    event_category: 'ats_score',
    event_label: 'analysis_error',
    ...additionalData
  });
};
